import {
  type GiftCodeItem,
  type GiftCodeRedeemRequest,
  GiftCodeRedeemResponseSchema,
  type ShopCheckoutRequest,
  type ShopCheckoutResponse,
  ShopCheckoutResponseSchema,
  type ShopOrder,
  ShopOrderResponseSchema,
  ShopOrdersResponseSchema,
} from '@shefira/shared';

import { call } from './account.js';

/**
 * The shop's endpoints (ADR 0031). The character token says who buys; the
 * server opens the Checkout and, once Stripe has said it was paid, puts the
 * items in the bag. Nothing here decides anything: it asks and shows.
 */

/**
 * Open an order for an offer, once the buyer ticked the waiver of the right
 * of withdrawal (ADR 0044): the request carries it, the server refuses
 * without it.
 */
export async function startCheckout(token: string, offerId: string): Promise<ShopCheckoutResponse> {
  const body: ShopCheckoutRequest = { offerId, withdrawalWaiver: true };
  return call('/shop/checkout', ShopCheckoutResponseSchema, { method: 'POST', token, body });
}

export async function fetchOrders(token: string): Promise<ShopOrder[]> {
  const response = await call('/shop/orders', ShopOrdersResponseSchema, { method: 'GET', token });
  return response.orders;
}

export async function fetchOrder(token: string, orderId: string): Promise<ShopOrder> {
  const response = await call(
    `/shop/orders/${encodeURIComponent(orderId)}`,
    ShopOrderResponseSchema,
    {
      method: 'GET',
      token,
    },
  );
  return response.order;
}

/** A paid order the bag could not take: ask for it again once room was made. */
export async function claimOrder(token: string, orderId: string): Promise<ShopOrder> {
  const response = await call(
    `/shop/orders/${encodeURIComponent(orderId)}/claim`,
    ShopOrderResponseSchema,
    { method: 'POST', token },
  );
  return response.order;
}

/** The payment window was closed unpaid: the server closes the Checkout and the order. */
export async function cancelOrder(token: string, orderId: string): Promise<ShopOrder> {
  const response = await call(
    `/shop/orders/${encodeURIComponent(orderId)}/cancel`,
    ShopOrderResponseSchema,
    { method: 'POST', token },
  );
  return response.order;
}

/** A gift code (ADR 0063): the server checks it and puts its items in the bag; here is what it gave. */
export async function redeemGiftCode(token: string, code: string): Promise<GiftCodeItem[]> {
  const body: GiftCodeRedeemRequest = { code };
  const response = await call('/shop/redeem', GiftCodeRedeemResponseSchema, {
    method: 'POST',
    token,
    body,
  });
  return response.items;
}

/**
 * What the address bar says about a purchase on the way back from Stripe:
 * `?order=<id>` after paying, `?shop=cancelled` after giving up. Read once
 * and wiped from the address, so a reload does not replay it.
 */
export function consumeShopReturn(): { order: string } | { cancelled: true } | null {
  const params = new URLSearchParams(window.location.search);
  const order = params.get('order');
  const shop = params.get('shop');
  if (order === null && shop !== 'cancelled') return null;
  params.delete('order');
  params.delete('shop');
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query === '' ? '' : `?${query}`}`,
  );
  return order !== null ? { order } : { cancelled: true };
}
